import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOrders, useSignedWithoutDeposit } from '@saiens/shared/hooks';
import { formatCurrency, formatDate, getSaleStateLabel } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, ChevronRight } from 'lucide-react';

const DEPOSIT_TAB = 'signed_without_deposit';

export function SaleListPage() {
  const [searchParams] = useSearchParams();
  const depositMode = searchParams.get('filter') === 'deposit';

  const [search, setSearch] = useState('');
  const [state, setState] = useState<string>(depositMode ? DEPOSIT_TAB : '');
  const [myOrders, setMyOrders] = useState(true);
  const [createdByMe, setCreatedByMe] = useState(false);

  const isDepositTab = state === DEPOSIT_TAB;

  const regularQuery = useOrders({
    search,
    state: isDepositTab ? undefined : (state || undefined),
    myOrders: createdByMe ? false : myOrders,
    createdByMe,
    limit: 50,
  });

  const depositQuery = useSignedWithoutDeposit({ limit: 50 });

  const { data, isLoading, error } = isDepositTab ? depositQuery : regularQuery;

  return (
    <div className="px-4 py-6">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">
          {depositMode ? '待收訂金報價單' : '銷售訂單'}
        </h1>
      </div>

      {/* Search & Filter */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋訂單..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto">
          <FilterChip
            label="我的訂單"
            active={myOrders && !createdByMe}
            onClick={() => {
              // 從「我協助的」切回來時直接啟用；否則維持原本的 toggle 行為
              setMyOrders(createdByMe ? true : !myOrders);
              setCreatedByMe(false);
            }}
          />
          <FilterChip
            label="我協助的"
            active={createdByMe}
            onClick={() => {
              setCreatedByMe(!createdByMe);
              if (!createdByMe) setMyOrders(false);
            }}
          />
          {depositMode && (
            <FilterChip
              label="待收訂金"
              active={isDepositTab}
              onClick={() => setState(DEPOSIT_TAB)}
            />
          )}
          <FilterChip
            label="全部狀態"
            active={state === ''}
            onClick={() => setState('')}
          />
          <FilterChip
            label="報價單"
            active={state === 'draft'}
            onClick={() => setState('draft')}
          />
          <FilterChip
            label="已送出"
            active={state === 'sent'}
            onClick={() => setState('sent')}
          />
          <FilterChip
            label="已確認"
            active={state === 'sale'}
            onClick={() => setState('sale')}
          />
          {!depositMode && (
            <FilterChip
              label="待收訂金"
              active={isDepositTab}
              onClick={() => setState(DEPOSIT_TAB)}
            />
          )}
          <FilterChip
            label="完成"
            active={state === 'done'}
            onClick={() => setState('done')}
          />
        </div>
      </div>

      {/* List */}
      <div className="rounded-xl bg-white shadow-sm">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : data?.items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">沒有符合條件的訂單</div>
        ) : (
          <div className="divide-y">
            {data?.items.map((order) => (
              <Link
                key={order.id}
                to={`/sale/${order.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{order.name}</p>
                  <p className="text-sm text-gray-500 truncate">
                    {order.partner_name || '未指定客戶'}
                    {order.project_name && ` • ${order.project_name}`}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                    <span>{formatDate(order.date_order)}</span>
                    {order.user_name && <span>• {order.user_name}</span>}
                  </div>
                </div>
                <div className="ml-4 flex items-center gap-3">
                  <div className="text-right">
                    {order.state === 'sale' || order.state === 'done' ? (
                      <>
                        <p className="text-sm font-medium text-gray-800">
                          {formatCurrency(order.amount_total)}
                        </p>
                        <div className="mt-0.5 space-y-0.5">
                          {order.amount_invoiced > 0 && (
                            <p className="text-xs text-green-600">
                              過帳 {formatCurrency(order.amount_invoiced)}
                            </p>
                          )}
                          {order.amount_draft_invoice > 0 && (
                            <p className="text-xs text-amber-500">
                              草稿 {formatCurrency(order.amount_draft_invoice)}
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      <p className="text-sm font-medium text-primary">
                        {formatCurrency(order.amount_total)}
                      </p>
                    )}
                    <StateTag state={order.state} />
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Total count */}
      {data && (
        <p className="mt-4 text-center text-sm text-gray-500">共 {data.total} 筆訂單</p>
      )}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

function StateTag({ state }: { state: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    sale: 'bg-green-100 text-green-700',
    done: 'bg-purple-100 text-purple-700',
    cancel: 'bg-red-100 text-red-700',
  };

  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${colors[state] || colors.draft}`}>
      {getSaleStateLabel(state)}
    </span>
  );
}
